import type { TFunction } from 'i18next';
import { ArrowRight, LifeBuoy, Lightbulb, Search, UserRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { DependencyStatus, Module, ModuleHealth } from '@/schemas/commons';

import { ModulesLoading } from './Modules.loading';
import { useModules } from './Modules.requests';
import { styles } from './Modules.styles';

const HEALTH_LABEL_KEY: Record<DependencyStatus, string> = {
	up: 'HEALTH_UP',
	degraded: 'HEALTH_DEGRADED',
	down: 'HEALTH_DOWN',
	disabled: 'HEALTH_DISABLED',
};

const HEALTH_PILL: Record<DependencyStatus, { container: string; dot: string }> = {
	up: { container: styles.statusActive, dot: styles.dotSuccess },
	degraded: { container: styles.statusDegraded, dot: styles.dotWarn },
	down: { container: styles.statusDown, dot: styles.dotDown },
	disabled: { container: styles.statusMuted, dot: styles.dotMuted },
};

const buildHealthTooltip = (health: ModuleHealth, t: TFunction): string => {
	const lines = health.dependencies.map((dep) => {
		const role = dep.required ? t('HEALTH_DEP_REQUIRED') : t('HEALTH_DEP_OPTIONAL');
		const label = t(HEALTH_LABEL_KEY[dep.status]);
		const latency = dep.latencyMs != null ? ` · ${dep.latencyMs}ms` : '';
		const message = dep.message ? ` — ${dep.message}` : '';

		return `• ${dep.name} (${role}): ${label}${latency}${message}`;
	});

	return [t('HEALTH_TOOLTIP_INTRO'), ...lines].join('\n');
};

const formatCurrency = (value: number): string => `$${value.toFixed(2)}`;

export const Modules = () => {
	const { t } = useTranslation('translation', { keyPrefix: 'modules' });
	const navigate = useNavigate();
	const { data: modules, isLoading, isError } = useModules();

	if (isLoading) return <ModulesLoading />;
	if (isError || !modules) {
		return (
			<main className={styles.content}>
				<p className="text-[13px] text-error">{t('ERROR_LOAD')}</p>
			</main>
		);
	}

	const totals = modules.reduce(
		(acc, m) => ({
			last24h: acc.last24h + m.costs.last24h,
			last7d: acc.last7d + m.costs.last7d,
			currentMonth: acc.currentMonth + m.costs.currentMonth,
			agents: acc.agents + m.agents.length,
			tools: acc.tools + m.agents.reduce((sum, a) => sum + a.tools.length, 0),
		}),
		{ last24h: 0, last7d: 0, currentMonth: 0, agents: 0, tools: 0 },
	);

	return (
		<>
			<header className={styles.topbar}>
				<nav className="flex items-center gap-3xs text-[12px] text-subtle">
					<span className="font-medium text-default">{t('BREADCRUMB')}</span>
				</nav>
			</header>

			<main className={styles.content}>
				<header className="flex flex-wrap items-end justify-between gap-md">
					<div>
						<p className={styles.pageEyebrow}>{t('EYEBROW')}</p>
						<h1 className={styles.pageH1}>{t('TITLE')}</h1>
						<p className={styles.pageSub}>
							{t('SUMMARY', {
								modules: modules.length,
								agents: totals.agents,
								tools: totals.tools,
								monthCost: formatCurrency(totals.currentMonth),
							})}
						</p>
					</div>
					<div className="relative w-[288px]">
						<Search className="pointer-events-none absolute left-xs top-1/2 h-[14px] w-[14px] -translate-y-1/2 text-subtle" />
						<input
							type="search"
							placeholder={t('SEARCH_PLACEHOLDER')}
							className="w-full rounded-md border border-subtle bg-white px-xs py-2xs pl-xl text-[13px] text-default placeholder:text-subtle focus:border-action-primary focus:outline-none focus:ring-2 focus:ring-action-primary/15"
						/>
					</div>
				</header>

				<section>
					<table className="w-full text-left text-[13px]">
						<thead className="border-b border-subtle">
							<tr>
								<th scope="col" className={`${styles.tableHeader} px-sm py-xs`}>
									{t('COL_MODULE')}
								</th>
								<th scope="col" className={`${styles.tableHeader} px-sm py-xs`}>
									{t('COL_AGENTS_TOOLS')}
								</th>
								<th scope="col" className={`${styles.tableHeader} px-sm py-xs text-right`}>
									{t('COL_24H')}
								</th>
								<th scope="col" className={`${styles.tableHeader} px-sm py-xs text-right`}>
									{t('COL_7D')}
								</th>
								<th scope="col" className={`${styles.tableHeader} px-sm py-xs text-right`}>
									{t('COL_MONTH')}
								</th>
								<th scope="col" className={`${styles.tableHeader} px-sm py-xs`}>
									{t('COL_STATUS')}
								</th>
								<th scope="col" className={`${styles.tableHeader} px-sm py-xs`} aria-label="action" />
							</tr>
						</thead>
						<tbody>
							{modules.map((mod) => (
								<ModuleRow key={mod.name} mod={mod} onClick={() => navigate(`/modules/${mod.name}`)} />
							))}
							<tr className="border-t border-subtle">
								<td colSpan={2} className="px-sm py-sm text-right text-[11px] font-medium text-subtle">
									{t('TOTAL_LABEL')}
								</td>
								<td className="px-sm py-sm text-right font-semibold tabular-nums text-default">
									{formatCurrency(totals.last24h)}
								</td>
								<td className="px-sm py-sm text-right font-semibold tabular-nums text-default">
									{formatCurrency(totals.last7d)}
								</td>
								<td className="px-sm py-sm text-right font-semibold tabular-nums text-purple-700">
									{formatCurrency(totals.currentMonth)}
								</td>
								<td colSpan={2} />
							</tr>
						</tbody>
					</table>
				</section>

				<aside className={styles.reference}>
					<Lightbulb className="mt-3xs h-md w-md shrink-0 text-subtle" />
					<div className="flex flex-col gap-3xs">
						<p className="text-[12px] leading-tight text-default">{t('REFERENCE_TEXT')}</p>
						<p className="text-[11px] text-subtle">{t('REFERENCE_NOTE')}</p>
					</div>
				</aside>
			</main>
		</>
	);
};

type ModuleRowProps = {
	mod: Module;
	onClick: () => void;
};

const ModuleRow = ({ mod, onClick }: ModuleRowProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'modules' });
	const totalTools = mod.agents.reduce((sum, a) => sum + a.tools.length, 0);
	const healthStatus = mod.health?.status;
	const pill = healthStatus ? HEALTH_PILL[healthStatus] : null;
	const healthLabel = healthStatus ? t(HEALTH_LABEL_KEY[healthStatus]) : t('HEALTH_UNKNOWN');
	const tooltip = mod.health ? buildHealthTooltip(mod.health, t) : undefined;

	return (
		<tr onClick={onClick} className="group cursor-pointer border-b border-subtle/50 transition-colors hover:bg-grey-25">
			<td className="px-sm py-md">
				<div className="flex items-center gap-xs">
					<span className={styles.moduleMark}>
						<LifeBuoy className="h-md w-md" />
					</span>
					<div className="flex flex-col gap-3xs leading-tight">
						<span className="text-[14px] font-semibold text-default">{mod.name}</span>
						<span className="text-[11px] font-mono leading-none text-subtle">{mod.path}</span>
						<p className="mt-3xs max-w-md text-[11px] leading-tight text-subtle">{mod.description}</p>
					</div>
				</div>
			</td>
			<td className="px-sm py-md">
				<div className="flex flex-col gap-3xs">
					{mod.agents.map((agent) => (
						<div key={agent.name} className="flex flex-col gap-3xs">
							<span className="inline-flex items-center gap-3xs text-[11px] text-subtle">
								<UserRound className="h-sm w-sm" />
								<code className="font-mono text-default">{agent.name}</code>
							</span>
							<div className="flex flex-wrap gap-3xs">
								{agent.tools.map((tool) => (
									<span key={tool.name} className={styles.tag}>
										{tool.name}
									</span>
								))}
							</div>
						</div>
					))}
					{totalTools === 0 && <span className="text-[11px] text-subtle">—</span>}
				</div>
			</td>
			<td className="px-sm py-md text-right align-middle">
				<div className="leading-tight">
					<p className={styles.costMain}>{formatCurrency(mod.costs.last24h)}</p>
				</div>
			</td>
			<td className="px-sm py-md text-right align-middle">
				<div className="leading-tight">
					<p className={styles.costMain}>{formatCurrency(mod.costs.last7d)}</p>
				</div>
			</td>
			<td className="px-sm py-md text-right align-middle">
				<div className="leading-tight">
					<p className={styles.costBrand}>{formatCurrency(mod.costs.currentMonth)}</p>
					<p className={styles.costDeltaFlat}>proy. {formatCurrency(mod.costs.monthProjection)}</p>
				</div>
			</td>
			<td className="px-sm py-md align-middle">
				<span className={pill ? pill.container : styles.statusMuted} title={tooltip}>
					<span className={pill ? pill.dot : styles.dotMuted} />
					{healthLabel}
				</span>
			</td>
			<td className="px-sm py-md text-right align-middle text-subtle">
				<ArrowRight className="ml-auto h-[14px] w-[14px] transition-colors group-hover:text-default" />
			</td>
		</tr>
	);
};
